import React, { useEffect, useMemo, useState } from "react";
import { collection, deleteDoc, doc, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import { db } from "../../../config/firebase";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useTheme } from "../../../contexts/ThemeContext";

import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../../templates/mobile/CardListMobile";
import { pode } from "../../../utils/permissoes";
import { SortableHeader, useSortableTable } from "../../../hooks/useSortableTable";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { NIVEIS } from "../../../config/niveis";
import { temNivelMinimo } from "../../../utils/nivelAcesso";

const TIPO_LABEL = {
  INSTALACAO: "Instalacao",
  OS: "OS",
};

function labelTipo(tipo) {
  const key = String(tipo || "").toUpperCase();
  return TIPO_LABEL[key] || (tipo ? String(tipo) : "-");
}

export default function ChecklistsPage() {
  const navigate = useNavigate();
  const { usuario: usuarioLogado, carregando: authLoading } = useAuth();
  const { empresaAtual } = useEmpresa();
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [checklists, setChecklists] = useState([]);

  const [campoFiltro, setCampoFiltro] = useState("codigo");
  const [textoFiltro, setTextoFiltro] = useState("");

  const isAdmin = temNivelMinimo(usuarioLogado, NIVEIS.ADMIN);
  const canTabela = (
    pode(usuarioLogado, "cadastros", "checklists", "tabela") ||
    pode(usuarioLogado, "checklists", "abertos", "tabela") ||
    pode(usuarioLogado, "checklists", "finalizados", "tabela") ||
    pode(usuarioLogado, "checklists", "calendario", "tabela") ||
    isAdmin
  );
  const canCriar = pode(usuarioLogado, "cadastros", "checklists", "form") || isAdmin;
  const canEditar = pode(usuarioLogado, "cadastros", "checklists", "altera") || isAdmin;
  const canExcluir = canEditar;

  async function carregar() {
    if (!usuarioLogado?.grupoId) return;
    setLoading(true);
    setErro("");
    try {
      const q = query(
        collection(db, "checklistsModelos"),
        where("grupoId", "==", usuarioLogado.grupoId)
      );
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setChecklists(data);
    } catch (e) {
      console.error(e);
      setErro("Falha ao carregar checklists (modelos).");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!authLoading) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, usuarioLogado?.grupoId]);

  const filtradas = useMemo(() => {
    const txt = textoFiltro.trim().toLowerCase();
    if (!txt) return checklists;

    return checklists.filter((c) => {
      if (campoFiltro === "tipo") {
        return labelTipo(c?.tipo).toLowerCase().includes(txt);
      }
      const v = String(c?.[campoFiltro] || "").toLowerCase();
      return v.includes(txt);
    });
  }, [checklists, campoFiltro, textoFiltro]);

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    filtradas,
    "codigo",
    "asc"
  );

  async function handleExcluir(id, label) {
    const ok = window.confirm(`Excluir o checklist modelo "${label}"?`);
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "checklistsModelos", id));
      await carregar();
    } catch (e) {
      console.error(e);
      window.alert("Nao foi possivel excluir. Verifique regras/permissoes do Firestore.");
    }
  }

  function handleEditar(id) {
    navigate(`/cadastros/checklists/editar/${id}`);
  }

  const cards = useMemo(() => {
    return sortedData.map((c) => ({
      id: c.id,
      nome: `${c.codigo || "-"} — ${c.nome || "Sem nome"}`,
      subtitulo: `${labelTipo(c.tipo)} • ${c.fabricante || "-"} / ${c.modelo || "-"}`,
      detalhes: [{ icone: "📋", label: "Itens", valor: String(c.itens?.length || 0) }],
    }));
  }, [sortedData]);

  const camposFiltro = [
    { value: "codigo", label: "Codigo" },
    { value: "nome", label: "Nome" },
    { value: "tipo", label: "Tipo" },
    { value: "fabricante", label: "Fabricante" },
    { value: "modelo", label: "Modelo" },
  ];

  const info = (
    <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>
      Aqui voce cadastra <b>checklists</b> (modelos). Eles podem ser usados na instalacao do equipamento
      ou em OS, conforme o tipo configurado.
      {empresaAtual?.nome ? (
        <>
          {" "}
          (Empresa atual: <b>{empresaAtual.nome}</b>)
        </>
      ) : null}
    </div>
  );

  const contentDesktop = (
    <div className="pc-table-wrapper">
      <table className="pc-table">
        <thead>
          <tr>
            <SortableHeader
              label="Codigo"
              sortKey="codigo"
              sortDirection={getSortDirection("codigo")}
              onSort={requestSort}
            />
            <SortableHeader
              label="Checklist (modelo)"
              sortKey="nome"
              sortDirection={getSortDirection("nome")}
              onSort={requestSort}
            />
            <SortableHeader
              label="Tipo"
              sortKey="tipo"
              sortDirection={getSortDirection("tipo")}
              onSort={requestSort}
            />
            <SortableHeader
              label="Fabricante"
              sortKey="fabricante"
              sortDirection={getSortDirection("fabricante")}
              onSort={requestSort}
            />
            <SortableHeader
              label="Modelo"
              sortKey="modelo"
              sortDirection={getSortDirection("modelo")}
              onSort={requestSort}
            />
            <th>Itens</th>
            <th style={{ width: 140 }}>Acoes</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((c) => (
            <tr key={c.id}>
              <td>{c.codigo || "-"}</td>
              <td>{c.nome || "-"}</td>
              <td>{labelTipo(c.tipo)}</td>
              <td>{c.fabricante || "-"}</td>
              <td>{c.modelo || "-"}</td>
              <td>{c.itens?.length || 0}</td>
              <td style={{ display: "flex", gap: 8 }}>
                {canEditar && (
                  <button className="pc-icon-btn" title="Editar" onClick={() => handleEditar(c.id)}>
                    ✏️
                  </button>
                )}
                {canExcluir && (
                  <button
                    className="pc-icon-btn"
                    title="Excluir"
                    onClick={() => handleExcluir(c.id, `${c.codigo || ""} ${c.nome || ""}`.trim())}
                  >
                    🗑️
                  </button>
                )}
              </td>
            </tr>
          ))}

          {sortedData.length === 0 && (
            <tr>
              <td colSpan={7} style={{ padding: 14, opacity: 0.75 }}>
                Nenhum checklist modelo encontrado.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const actionsDesktop = (
    <div style={{ display: "flex", gap: 10 }}>
      {canCriar && (
        <button className="pc-btn pc-btn-primary" onClick={() => navigate("/cadastros/checklists/novo")}>+ Novo Checklist</button>
      )}
      <button className="pc-btn" onClick={carregar}>
        Atualizar
      </button>
    </div>
  );

  if (!canTabela && !authLoading) {
    return (
      <LayoutDesktop
        title="Checklists (Modelos)"
        subtitle="Voce nao tem permissao para acessar esta tela."
      >
        <div style={{ padding: 16, opacity: 0.85 }}>Acesso negado.</div>
      </LayoutDesktop>
    );
  }

  if (authLoading || loading) {
    const Layout = isMobile ? LayoutMobile : LayoutDesktop;
    return (
      <Layout title="Checklists (Modelos)" subtitle="Cadastro por tipo/modelo">
        <div style={{ padding: 16, opacity: theme === "dark" ? 0.8 : 0.9 }}>Carregando...</div>
      </Layout>
    );
  }

  return isMobile ? (
    <LayoutMobile title="Checklists (Modelos)" subtitle="Cadastro por tipo/modelo">
      <FiltroBar
        campos={camposFiltro}
        campoSelecionado={campoFiltro}
        onCampoChange={setCampoFiltro}
        textoFiltro={textoFiltro}
        onTextoChange={setTextoFiltro}
        onAdicionarClick={canCriar ? () => navigate("/cadastros/checklists/novo") : undefined}
        mostrarAdicionar={!!canCriar}
      />
      {info}
      {erro ? <div className="pc-alert pc-alert-erro" style={{ marginTop: 8 }}>{erro}</div> : null}
      <CardListMobile cards={cards} onCardClick={(id) => handleEditar(id)} />
    </LayoutMobile>
  ) : (
    <LayoutDesktop
      title="Checklists (Modelos)"
      subtitle="Cadastro por tipo/modelo"
      actions={actionsDesktop}
    >
      <FiltroBar
        campos={camposFiltro}
        campoSelecionado={campoFiltro}
        onCampoChange={setCampoFiltro}
        textoFiltro={textoFiltro}
        onTextoChange={setTextoFiltro}
        onAdicionarClick={canCriar ? () => navigate("/cadastros/checklists/novo") : undefined}
        mostrarAdicionar={!!canCriar}
      />
      {info}
      {erro ? <div className="pc-alert pc-alert-erro" style={{ marginTop: 8 }}>{erro}</div> : null}
      {contentDesktop}
    </LayoutDesktop>
  );
}
